// ============================================================================
// 오디오 — Web Audio 직접 사용. 클립 재생 없음.
// 모든 재질음은 하나의 노이즈 소스에서 갈라진다 (§4.4).
//   noise ─┬─ cut      : bandpass → gain   (연속 절단음, 칼날 속도 추종)
//          ├─ crackle  : highpass → gain   (자글거림, 불규칙 짧은 파열)
//          ├─ shot     : bandpass → gain   (관통 / 완료 원샷)
//          ├─ friction : bandpass → gain   (카드 마찰 지속음)
//          ├─ click    : highpass → gain   (걸림 풀림 클릭)
//          ├─ thudN    : lowpass → gain    (착지 저음의 노이즈 성분)
//          ├─ page     : bandpass → gain   (바인더 시트 쓸림 지속음)
//          ├─ spark    : highpass → gain   (AR·UR 이 드러나는 순간의 반짝임)
//          └─ touch    : highpass → gain   (접촉 순간 아주 작은 틱)
//   osc(sine) ─ thud body (임팩트 몸통. 재질음이 아니라 충격음이라 별도 소스 허용)
// ============================================================================
import { P } from './params';
import { bgm } from './bgm';

function makeNoiseBuffer(ctx: AudioContext, seconds = 2): AudioBuffer {
  const n = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, n, ctx.sampleRate);
  const d = buf.getChannelData(0);
  // 결정적 노이즈 (xorshift) — 재현 가능
  let s = 0x9e3779b9;
  for (let i = 0; i < n; i++) {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    d[i] = ((s >>> 0) / 4294967296) * 2 - 1;
  }
  return buf;
}

interface Chain {
  filter: BiquadFilterNode;
  gain: GainNode;
}

export class AudioEngine {
  ctx: AudioContext | null = null;
  master: GainNode | null = null;
  private cut!: Chain;
  private crackle!: Chain;
  private shot!: Chain;
  private friction!: Chain;
  private click!: Chain;
  private thudN!: Chain;
  private touch!: Chain;
  private page!: Chain;
  private spark!: Chain;
  private rare!: Chain;
  private rareTone!: GainNode;
  private rareOsc!: OscillatorNode;
  private thudGain!: GainNode;
  private rarePhase = 0;
  lastRareLevel = 0;
  ready = false;
  muted = false;
  private crackleAcc = 0;
  private rng = 12345;

  // 진단
  lastCutLevel = 0;
  lastFrictionLevel = 0;
  lastPageLevel = 0;
  crackleRate = 0;

  private rand(): number {
    this.rng ^= this.rng << 13;
    this.rng ^= this.rng >>> 17;
    this.rng ^= this.rng << 5;
    return (this.rng >>> 0) / 4294967296;
  }

  /** 첫 사용자 입력 시 호출. 컨텍스트를 깨우고 무음을 한 번 흘려 웜업. */
  warmup(): void {
    if (this.ctx) {
      if (this.ctx.state !== 'running') void this.ctx.resume();
      return;
    }
    const ctx = new AudioContext({ latencyHint: 'interactive' });
    this.ctx = ctx;
    const master = ctx.createGain();
    master.gain.value = P.sound.master;
    master.connect(ctx.destination);
    this.master = master;

    const noise = ctx.createBufferSource();
    noise.buffer = makeNoiseBuffer(ctx);
    noise.loop = true;

    const mk = (type: BiquadFilterType, freq: number, q: number): Chain => {
      const filter = ctx.createBiquadFilter();
      filter.type = type;
      filter.frequency.value = freq;
      filter.Q.value = q;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      noise.connect(filter);
      filter.connect(gain);
      gain.connect(master);
      return { filter, gain };
    };
    this.cut = mk('bandpass', P.sound.cut.freqMin, P.sound.cut.q);
    this.crackle = mk('highpass', P.sound.crackle.hpf, 0.7);
    this.shot = mk('bandpass', P.sound.penetrate.freq, 1.0);
    this.friction = mk('bandpass', P.sound.friction.freqMin, P.sound.friction.q);
    this.click = mk('highpass', P.sound.click.hpf, 0.7);
    this.thudN = mk('lowpass', 220, 0.8);
    this.touch = mk('highpass', 5000, 0.7);
    this.page = mk('bandpass', P.sound.page.freqMin, P.sound.page.q);
    // **자기 체인을 준다.** click/touch 를 빌려 쓰면 burst 가 서로의 예약을 취소해
    // 걸림 클릭이 먹히거나 반짝임이 잘린다 (burst 는 cancelScheduledValues 를 부른다).
    this.spark = mk('highpass', P.sound.reveal.hpf, 0.7);
    this.thudGain = ctx.createGain();
    this.thudGain.gain.value = 0;
    this.thudGain.connect(master);

    // 레어 예고 리저: 노이즈 밴드가 위로 쓸려 올라가고, 저음 사인이 같이 올라온다.
    this.rare = mk('bandpass', P.sound.rare.freqMin, P.sound.rare.qMin);
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = P.sound.rare.toneMin;
    this.rareTone = ctx.createGain();
    this.rareTone.gain.value = 0;
    osc.connect(this.rareTone);
    this.rareTone.connect(master);
    osc.start();
    this.rareOsc = osc;

    noise.start();
    // 무음 웜업
    const silent = ctx.createBufferSource();
    silent.buffer = ctx.createBuffer(1, 256, ctx.sampleRate);
    silent.connect(ctx.destination);
    silent.start();
    if (ctx.state !== 'running') void ctx.resume();
    this.ready = true;
    // BGM 시퀀서는 같은 컨텍스트·같은 master 아래로 들어간다 (음소거·마스터 볼륨 공유)
    bgm.attach(ctx, master);
  }

  get latencyMs(): number {
    const c = this.ctx as (AudioContext & { outputLatency?: number }) | null;
    if (!c) return -1;
    return ((c.baseLatency || 0) + (c.outputLatency || 0)) * 1000;
  }

  private now(): number {
    return this.ctx!.currentTime;
  }

  setMaster(): void {
    if (!this.master) return;
    this.master.gain.setTargetAtTime(this.muted ? 0 : P.sound.master, this.now(), 0.01);
  }

  // ---------------------------------------------------------------- 연속음
  /** 절단 연속음. speed: 칼날 속도(unit/s), bright: 0..1 (빠른 속도 추정 기반) */
  setCut(speed: number, bright: number): void {
    if (!this.ready) return;
    const s = P.sound.cut;
    const lvl = Math.min(1, speed / s.speedForMax);
    const level = s.gain * Math.sqrt(lvl);
    const t = this.now();
    this.cut.gain.gain.setTargetAtTime(level, t, level > this.lastCutLevel ? s.attack : s.release);
    this.cut.filter.frequency.setTargetAtTime(s.freqMin + (s.freqMax - s.freqMin) * bright, t, 0.01);
    this.cut.filter.Q.value = s.q;
    this.lastCutLevel = level;
  }

  /** 카드 마찰 지속음. speed: 미끄러짐 속도(unit/s) */
  setFriction(speed: number): void {
    if (!this.ready) return;
    const s = P.sound.friction;
    const lvl = Math.min(1, speed / s.speedForMax);
    const level = s.gain * Math.sqrt(lvl);
    const t = this.now();
    this.friction.gain.gain.setTargetAtTime(level, t, level > this.lastFrictionLevel ? s.attack : s.release);
    this.friction.filter.frequency.setTargetAtTime(s.freqMin + (s.freqMax - s.freqMin) * lvl, t, 0.01);
    this.friction.filter.Q.value = s.q;
    this.lastFrictionLevel = level;
  }

  /**
   * 바인더 시트 쓸림 지속음. speed: 시트 가장자리 선속도 (unit/s)
   * friction 과 같은 구조지만 더 낮고 꼬리가 길다 — 카드가 아니라 큰 판이기 때문이다.
   */
  setPage(speed: number): void {
    if (!this.ready) return;
    const s = P.sound.page;
    const lvl = Math.min(1, Math.max(0, speed) / s.speedForMax);
    const level = s.gain * Math.sqrt(lvl);
    const t = this.now();
    this.page.gain.gain.setTargetAtTime(level, t, level > this.lastPageLevel ? s.attack : s.release);
    this.page.filter.frequency.setTargetAtTime(s.freqMin + (s.freqMax - s.freqMin) * lvl, t, 0.01);
    this.page.filter.Q.value = s.q;
    this.lastPageLevel = level;
  }

  /**
   * 레어 예고 리저. 물리 스텝마다 호출. level: 0..1 (드러난 진행도, 0 이면 무음)
   * 진동은 세기 조절이 없어 켜짐/꺼짐뿐이라, 고조는 이 소리가 맡는다.
   */
  setRare(level: number, dt: number): void {
    if (!this.ready) return;
    const s = P.sound.rare;
    const L = Math.max(0, Math.min(1, level));
    const t = Math.pow(L, Math.max(0.1, s.curve));
    // 맥동: 대역 안에서 음량이 흔들려 두근거림처럼 들린다
    this.rarePhase += dt * s.pulseRate;
    const wob = s.pulseRate > 0 ? 1 - s.pulseDepth * 0.5 * (1 - Math.cos(this.rarePhase * 2 * Math.PI)) : 1;
    const target = s.gain * t * wob;
    const now = this.now();
    const tau = target > this.lastRareLevel ? s.attack : s.release;
    this.rare.gain.gain.setTargetAtTime(target, now, tau);
    this.rare.filter.frequency.setTargetAtTime(s.freqMin + (s.freqMax - s.freqMin) * t, now, 0.02);
    this.rare.filter.Q.setTargetAtTime(s.qMin + (s.qMax - s.qMin) * t, now, 0.05);
    this.rareTone.gain.setTargetAtTime(s.toneGain * t * wob, now, tau);
    this.rareOsc.frequency.setTargetAtTime(s.toneMin + (s.toneMax - s.toneMin) * t, now, 0.05);
    this.lastRareLevel = target;
    if (L <= 0) this.rarePhase = 0;
  }

  /** 자글거림 스케줄러. 물리 스텝마다 호출. progressSpeed: 0..1 */
  tickCrackle(progress01: number, dt: number): void {
    if (!this.ready) return;
    const s = P.sound.crackle;
    if (progress01 <= 0.001) {
      this.crackleRate = 0;
      return;
    }
    const rate = s.rateMin + (s.rateMax - s.rateMin) * progress01;
    this.crackleRate = rate;
    // 포아송 근사: 불규칙 간격
    this.crackleAcc += rate * dt;
    while (this.crackleAcc >= 1) {
      this.crackleAcc -= 0.5 + this.rand() * 1.0; // 간격을 불규칙하게
      const amp = s.gain * (0.35 + this.rand() * 0.65) * (0.4 + 0.6 * progress01);
      const dur = s.durMin + (s.durMax - s.durMin) * this.rand();
      this.burst(this.crackle, amp, dur);
    }
    this.crackle.filter.frequency.value = s.hpf;
  }

  private burst(ch: Chain, amp: number, dur: number, freq?: number): void {
    const t = this.now() + 0.001;
    const g = ch.gain.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(amp, t + 0.0008);
    g.exponentialRampToValueAtTime(0.0005, t + dur);
    g.setValueAtTime(0, t + dur + 0.001);
    if (freq !== undefined) ch.filter.frequency.setValueAtTime(freq, t);
  }

  // ---------------------------------------------------------------- 원샷
  penetrate(): void {
    if (!this.ready) return;
    const s = P.sound.penetrate;
    this.burst(this.shot, s.gain, s.dur, s.freq);
  }

  complete(): void {
    if (!this.ready) return;
    const s = P.sound.complete;
    this.burst(this.shot, s.gain, s.dur, s.freq);
  }

  /** 걸림 풀림 클릭. strength 0..1 (저장된 휘어짐 힘 비례) */
  clickRelease(strength: number): void {
    if (!this.ready) return;
    const s = P.sound.click;
    this.click.filter.frequency.value = s.hpf;
    this.burst(this.click, s.gain * (0.4 + 0.6 * Math.min(1, strength)), s.dur);
  }

  /** 착지 저음 */
  thud(strength = 1): void {
    this.thudWith(P.sound.thud, strength);
  }

  /** 시트가 더미에 닿는 소리. thud 와 같은 구조, 더 낮고 짧게. */
  pageSlap(strength = 1): void {
    this.thudWith(P.sound.pageSlap, strength);
  }

  /** thud 계열의 공용 몸통 (노이즈 성분 + 사인 임팩트). */
  private thudWith(s: { gain: number; freq: number; dur: number; noise: number }, strength: number): void {
    if (!this.ready || !this.ctx) return;
    const t = this.now() + 0.001;
    // 노이즈 성분
    this.burst(this.thudN, s.gain * s.noise * strength, s.dur * 0.6);
    // 사인 몸통
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(s.freq * 1.6, t);
    osc.frequency.exponentialRampToValueAtTime(s.freq, t + s.dur * 0.5);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(s.gain * strength, t + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0005, t + s.dur);
    osc.connect(g);
    g.connect(this.master!);
    osc.start(t);
    osc.stop(t + s.dur + 0.02);
  }

  /**
   * AR·UR 이 드러나는 순간의 타격음. **원샷, 실시간 합성** (클립 재생 금지 규칙).
   * strength 0..1 — 등급이 높을수록 크다 (P.reveal.sound × urScale 이 들어온다).
   *
   * 두 겹이다:
   *   반짝임 — 노이즈 하이패스 짧은 버스트. 다른 재질음과 같은 노이즈 버퍼에서 갈라진다 (§4.4)
   *   종     — 사인 배음 셋(1 / 1.5 / 2). 같은 포락선으로 감쇠하고 glide 만큼 살짝 올라간다.
   *            배음이 높을수록 빨리 꺼져야 금속처럼 들린다 (dur 에 가중치를 곱한다).
   * 예고 리저(setRare)와 달리 상태가 없다 — 부르는 쪽이 한 번만 부른다.
   */
  revealChime(strength = 1): void {
    if (!this.ready || !this.ctx) return;
    const s = P.sound.reveal;
    const amp = s.gain * Math.max(0, Math.min(1, strength));
    if (amp <= 0.0005) return;
    this.spark.filter.frequency.value = s.hpf;
    this.burst(this.spark, amp * s.shimmer, Math.max(0.005, s.shimmerDur));
    const t = this.now() + 0.001;
    const dur = Math.max(0.05, s.dur);
    const partials: [number, number, number][] = [
      // [주파수 배수, 음량 비, 길이 비]
      [1, 1.0, 1.0],
      [1.5, 0.5, 0.7],
      [2, 0.26, 0.45],
    ];
    for (const [mul, lvl, len] of partials) {
      const f0 = s.freq * mul;
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(f0, t);
      osc.frequency.exponentialRampToValueAtTime(f0 * Math.max(0.5, s.glide), t + dur * len);
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(amp * lvl, t + 0.004);
      g.gain.exponentialRampToValueAtTime(0.0005, t + dur * len);
      osc.connect(g);
      g.connect(this.master!);
      osc.start(t);
      osc.stop(t + dur * len + 0.02);
    }
  }

  /** 접촉 틱 (손가락이 닿는 순간, 20ms 예산 안의 첫 소리) */
  touchTick(): void {
    if (!this.ready) return;
    const s = P.sound.touch;
    if (s.gain <= 0) return;
    this.burst(this.touch, s.gain, s.dur);
  }

  /** 모든 연속음 즉시 끄기 (리셋 등) */
  silenceContinuous(): void {
    if (!this.ready) return;
    const t = this.now();
    this.cut.gain.gain.setTargetAtTime(0, t, 0.005);
    this.friction.gain.gain.setTargetAtTime(0, t, 0.005);
    this.page.gain.gain.setTargetAtTime(0, t, 0.005);
    this.rare.gain.gain.setTargetAtTime(0, t, 0.01);
    this.rareTone.gain.setTargetAtTime(0, t, 0.01);
    this.lastCutLevel = 0;
    this.lastFrictionLevel = 0;
    this.lastPageLevel = 0;
    this.lastRareLevel = 0;
    this.rarePhase = 0;
    this.crackleRate = 0;
  }
}

export const audio = new AudioEngine();
